import React, { useEffect, useRef, useState, useMemo } from "react";
import { Message } from "../../../services/chatService/ChatService";
import { getMessageGroupingPositions, groupMessagesByDate } from "../../../utilities/chat/groupingUtils";
import MessageItem from "./MessageItem";
import { ValidateOtpResponseDto } from "../../../interfaces/ExtermalUserDtos/ExternalUsersDtos";
import CallLogMessage from "./callLogMessages/CallLogMessage";
import ChatMessageSkeleton from "./ChatMessageSkeleton";
import { useVisibleReactionRead } from "../../../hooks/globalChatHooks/useVisibleReactionRead";

const getUTCTime = (dateValue: string | Date | undefined): number => {
    if (!dateValue) return 0;
    if (dateValue instanceof Date) return dateValue.getTime();
    if (typeof dateValue === 'string') {
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return 0;
        return date.getTime();
    }
    return 0;
};

interface MessageListProps {
    conversationId: number;
    messages: Message[];
    currentUserId: string;
    onAddReaction: (messageId: number, reactionType: string) => void;
    externalUserDetails?: ValidateOtpResponseDto | null;
    scrollToBottomTrigger?: boolean;
    onLoadMore?: () => Promise<number>;
    loadingTop?: boolean;
    hasMore?: boolean;
    onEditMessage?: (messageId: number) => void;
    onDeleteMessage?: (messageId: number) => void;
    onOpenThread?: (message: Message) => void;
    onToggleBookmark?: (messageId: number) => void;
    onRetryMessage?: (messageId: number) => void;
    onCancelMessage?: (messageId: number) => void;
    compact?: boolean;
    appearance?: "default" | "meeting-dark";
    focusedMessageId?: number;
    onReadThrough?: (messageId: number) => void;
    readTrackingEnabled?: boolean;
}

const MessageList: React.FC<MessageListProps> = ({
    conversationId,
    messages,
    currentUserId,
    onAddReaction,
    externalUserDetails,
    scrollToBottomTrigger,
    onLoadMore,
    loadingTop = false,
    hasMore = true,
    onEditMessage,
    onDeleteMessage,
    onOpenThread,
    onToggleBookmark,
    onRetryMessage,
    onCancelMessage,
    compact = false,
    appearance = "default",
    focusedMessageId,
    onReadThrough,
    readTrackingEnabled = true,
}) => {
    const isMeetingDark = appearance === "meeting-dark";
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    useVisibleReactionRead(scrollContainerRef, conversationId, "data-chat-message-id", messages, readTrackingEnabled);
    const [toggledReactionMenuId, setToggledReactionMenuId] = useState<number | null>(null);
    const [visibleDateMessageIds, setVisibleDateMessageIds] = useState<number[]>([]);
    const [relativeTimeNow, setRelativeTimeNow] = useState(() => Date.now());
    const [highlightedMessageId, setHighlightedMessageId] = useState<number | null>(null);
    const revealedFocusedMessageRef = useRef<number | null>(null);


    useEffect(() => {
        const intervalId = window.setInterval(() => {
            setRelativeTimeNow(Date.now());
        }, 30_000);

        return () => window.clearInterval(intervalId);
    }, []);

    const isLoadingMoreRef = useRef(false);
    const prevFirstMsgIdRef = useRef<number | null>(null);

    const { displayMessages, callGroups } = useMemo(() => {
        const groups: Record<number, Message[]> = {};
        const display: Message[] = [];
        const seenCallIds = new Set<number>();

        const sorted = [...messages].sort((a, b) => getUTCTime(a.createDate) - getUTCTime(b.createDate));

        sorted.forEach(msg => {
            if (msg.messageType === 'SYSTEM' && msg.callRecordId && msg.callRecordId !== 0) {
                if (!groups[msg.callRecordId]) groups[msg.callRecordId] = [];
                groups[msg.callRecordId].push(msg);

                if (!seenCallIds.has(msg.callRecordId)) {
                    seenCallIds.add(msg.callRecordId);
                    display.push(msg);
                }
            } else {
                display.push(msg);
            }
        });

        return { displayMessages: display, callGroups: groups };
    }, [messages]);

    const groupedMessages = useMemo(() => {
        const groups = groupMessagesByDate(displayMessages);
        groups.forEach(group => {
            group.messages.sort((a, b) => getUTCTime(a.createDate) - getUTCTime(b.createDate));
        });
        return groups;
    }, [displayMessages]);

    const messageGroupingById = useMemo(() => {
        const positions = new Map<number, ReturnType<typeof getMessageGroupingPositions>[number]>();
        groupedMessages.forEach(group => {
            getMessageGroupingPositions(group.messages).forEach((position, index) => {
                positions.set(group.messages[index].id, position);
            });
        });
        return positions;
    }, [groupedMessages]);

    const handleToggleDateVisibility = (messageId: number) => {
        setVisibleDateMessageIds(prevIds =>
            prevIds.includes(messageId) ? prevIds.filter(id => id !== messageId) : [...prevIds, messageId]
        );
    };

    const handleToggleReactionMenu = (messageId: number) => {
        setToggledReactionMenuId(prevId => (prevId === messageId ? null : messageId));
    };

    const handleAddReactionAndClose = (messageId: number, reactionType: string) => {
        onAddReaction(messageId, reactionType);
        setToggledReactionMenuId(null);
    };

    const firstMsgId = messages.length > 0 ? messages[0].id : null;
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container || focusedMessageId) return;
        const prevFirst = prevFirstMsgIdRef.current;

        if (prevFirst === null) {
            setTimeout(() => { container.scrollTop = container.scrollHeight; }, 50);
        } else if (firstMsgId !== null && prevFirst !== null && firstMsgId >= prevFirst) {
            setTimeout(() => { container.scrollTop = container.scrollHeight; }, 50);
        }
        prevFirstMsgIdRef.current = firstMsgId;
    }, [focusedMessageId, firstMsgId, messages.length, scrollToBottomTrigger]);

    useEffect(() => {
        if (!focusedMessageId || !scrollContainerRef.current) return;
        if (revealedFocusedMessageRef.current === focusedMessageId) return;
        let frame = 0;
        let clearHighlightTimer = 0;
        let attempts = 0;
        const revealTarget = () => {
            const target = scrollContainerRef.current?.querySelector<HTMLElement>(`[data-chat-message-id="${focusedMessageId}"]`);
            if (!target) {
                attempts += 1;
                if (attempts < 30) frame = window.requestAnimationFrame(revealTarget);
                return;
            }
            const container = scrollContainerRef.current!;
            const containerRect = container.getBoundingClientRect();
            const targetRect = target.getBoundingClientRect();
            const targetTop = container.scrollTop + targetRect.top - containerRect.top
                - (container.clientHeight - targetRect.height) / 2;
            container.scrollTo({ top: Math.max(0, targetTop), behavior: 'auto' });
            revealedFocusedMessageRef.current = focusedMessageId;
            setHighlightedMessageId(focusedMessageId);
            clearHighlightTimer = window.setTimeout(() => setHighlightedMessageId(null), 3000);
        };
        frame = window.requestAnimationFrame(revealTarget);
        return () => {
            window.cancelAnimationFrame(frame);
            window.clearTimeout(clearHighlightTimer);
        };
    }, [focusedMessageId, messages]);

    useEffect(() => {
        if (revealedFocusedMessageRef.current !== focusedMessageId) {
            revealedFocusedMessageRef.current = null;
        }
    }, [focusedMessageId]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container || !onReadThrough || !readTrackingEnabled) return;

        let readTimer = 0;
        const messageTimes = new Map(displayMessages.map(message => [message.id, getUTCTime(message.createDate)]));
        const observer = new IntersectionObserver((entries) => {
            window.clearTimeout(readTimer);
            if (document.visibilityState !== "visible") return;

            const visibleIds = entries
                .filter(entry => entry.isIntersecting && entry.intersectionRatio >= 0.55)
                .map(entry => Number((entry.target as HTMLElement).dataset.chatMessageId))
                .filter(messageId => messageId > 0);
            if (visibleIds.length === 0) return;

            const latestVisibleMessageId = visibleIds.sort((left, right) =>
                (messageTimes.get(right) ?? 0) - (messageTimes.get(left) ?? 0) || right - left
            )[0];
            readTimer = window.setTimeout(() => {
                if (document.visibilityState === "visible") onReadThrough(latestVisibleMessageId);
            }, 400);
        }, { root: container, threshold: [0.55, 0.8, 1] });

        container.querySelectorAll<HTMLElement>("[data-chat-message-id]").forEach(element => observer.observe(element));
        return () => {
            window.clearTimeout(readTimer);
            observer.disconnect();
        };
    }, [displayMessages, onReadThrough, readTrackingEnabled]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container || !onLoadMore) return;
        let ticking = false;
        const handleScroll = async () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(async () => {
                try {
                    if (container.scrollTop <= 80 && hasMore && !loadingTop && !isLoadingMoreRef.current) {
                        const prevScrollHeight = container.scrollHeight;
                        isLoadingMoreRef.current = true;
                        await onLoadMore();
                        setTimeout(() => {
                            container.scrollTop = container.scrollHeight - prevScrollHeight;
                            isLoadingMoreRef.current = false;
                        }, 50);
                    }
                } finally { ticking = false; }
            });
        };
        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, [onLoadMore, hasMore, loadingTop]);

    return (
        // KEY FIX: Added !px-0 !pt-0 to override chat.css so full-width rows actually stretch edge to edge!
        <div role="log" aria-live="polite" aria-relevant="additions text" aria-label="Conversation messages" className={`chat_body_inner h-full overflow-y-auto custom-scrollbar !px-0 !pt-0 ${compact ? "pb-2" : "pb-3"} ${isMeetingDark ? "bg-slate-950" : "bg-white dark:bg-[#11131c]"}`} ref={scrollContainerRef}>
            {loadingTop && (
                <ChatMessageSkeleton rows={1} compact />
            )}

            {groupedMessages.map((group, index) => (
                <div key={group.dateLabel || index} className="flex flex-col pb-1">
                    
                    {/* Date Separator */}
                    <div className={`chat-date-separator relative flex items-center justify-center px-4 ${compact ? "my-2.5" : "my-3.5"}`}>
                        <div className="absolute inset-0 flex items-center px-4"><div className={`chat-date-separator-line w-full border-t ${isMeetingDark ? "border-slate-800" : "border-slate-200 dark:border-white/[0.06]"}`}></div></div>
                        <span className={`chat-date-separator-label relative px-3 text-[10px] font-semibold uppercase tracking-widest ${isMeetingDark ? "bg-slate-950 text-slate-500" : "bg-white text-slate-400 dark:bg-[#11131c] dark:text-slate-500"}`}>{group.dateLabel}</span>
                    </div>

                    <div className="flex flex-col gap-0">
                        {group.messages.map((msg, messageIndex) => {
                            const grouping = messageGroupingById.get(msg.id) ?? { groupedWithPrevious: false, groupedWithNext: false };
                            if (msg.messageType === 'SYSTEM' && msg.callRecordId && callGroups[msg.callRecordId]) {
                                const fullCallHistory = callGroups[msg.callRecordId];
                                return (
                                    <div className="px-4" key={`call-group-${msg.callRecordId}`}>
                                        <CallLogMessage messages={fullCallHistory} appearance={appearance} />
                                    </div>
                                );
                            }

                            return (
                                <div key={msg.id} data-chat-message-id={msg.id} className={`rounded-xl transition ${highlightedMessageId === msg.id ? 'bg-purple-50 ring-2 ring-inset ring-purple-300 dark:bg-purple-400/10' : ''}`}>
                                    <MessageItem
                                        message={msg}
                                        toggledReactionMenuId={toggledReactionMenuId}
                                        onToggleReactionMenu={handleToggleReactionMenu}
                                        onAddReaction={handleAddReactionAndClose}
                                        isDateVisible={visibleDateMessageIds.includes(msg.id)}
                                        onToggleDate={handleToggleDateVisibility}
                                        externalUserDetails={externalUserDetails}
                                        onEditMessage={onEditMessage}
                                        onDeleteMessage={onDeleteMessage}
                                        onOpenThread={onOpenThread}
                                        onToggleBookmark={onToggleBookmark}
                                        onRetryMessage={onRetryMessage}
                                        onCancelMessage={onCancelMessage}
                                        now={relativeTimeNow}
                                        compact={compact}
                                        appearance={appearance}
                                        groupedWithPrevious={grouping.groupedWithPrevious}
                                        groupedWithNext={grouping.groupedWithNext}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default MessageList;
